import React from 'react';
import type {
  ColumnDef,
  HeaderContext,
  CellContext,
} from '@tanstack/react-table';
import { ReactComponent as DownloadIcon } from 'assets/images/arrow-down.svg';
import { Checkbox } from 'components/ui/checkbox';
import { DataTableColumnHeader as Header } from 'components/ui/DataTable';
import { ScheduledReport } from 'contracts/scheduledRepports';
import { Button } from 'components/ui/ButtonGs';

export class DataTableUtils<T> {
  public buildColumn(props: {
    id?: string;
    accessorKey: keyof T;
    title: string;
    content: (row: T) => React.ReactNode;
  }): ColumnDef<T> {
    return {
      id: props.id ?? (props.accessorKey as string),
      accessorKey: props.accessorKey,
      header: ({ column }) => <Header column={column} title={props.title} />,
      cell: ({ row }) => props.content(row.original),
    };
  }

  public buildSelectionColumn(
    props: {
      id: string;
    } = { id: 'selection' }
  ): ColumnDef<T> {
    return {
      id: props.id,
      header: ({ table }: HeaderContext<T, unknown>) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && 'indeterminate')
          }
          onCheckedChange={(value) => table.toggleAllRowsSelected(!!value)}
          aria-label="Select all"
        />
      ),
      cell: ({ row }: CellContext<T, unknown>) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
        />
      ),
    };
  }

  /**
   * Builds a column showing the report status and a download button when the status is 'finished'
   */
  public buildStatusColumnForScheduledReports(props: {
    id?: string;
    accessorKey?: keyof T;
    title?: string;
    onDownloadButtonClick: (reportId: number) => void;
  }): ColumnDef<T> {
    return {
      id: props.id ?? (props.accessorKey as string),
      accessorKey: props.accessorKey ?? 'status',
      header: ({ column }) => (
        <Header column={column} title={props?.title ?? 'Status'} />
      ),
      cell: ({ row }: CellContext<T, unknown>) => {
        const { status, download_url, id } = row.original as ScheduledReport;

        const statusText = (() => {
          switch (status) {
            case 'pending':
              return 'Pendente';
            case 'processing':
              return 'Processando';
            case 'finished':
              return 'Finalizado';
            case 'failed':
              return 'Falhou';
            case 'canceled':
              return 'Cancelado';
          }
        })();

        if (status === 'finished' && download_url) {
          return (
            <Button.Root
              size="xs"
              variant="tertiary"
              className="pl-2 justify-start"
              onClick={() => props.onDownloadButtonClick(id)}
            >
              {statusText}
              <Button.Icon>
                <DownloadIcon className="w-4 h-4 !fill-current" />
              </Button.Icon>
            </Button.Root>
          );
        }

        return (
          <div className="pl-2 text-sm flex items-center">{statusText}</div>
        );
      },
    };
  }
}
